import { useMemo } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  Phone,
  Building2,
} from "lucide-react";
import {
  type Number,
  numberApi,
  type CreateNumberData,
  type UpdateNumberData,
} from "../services/numberApi";
import { NumberForm } from "../components/NumberForm";
import { companyApi } from "../services/companyApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNumberUIStore } from "@/stores/numberStore";
import { Loader } from "@/components/Loader";

export function NumbersPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    searchTerm,
    sourceFilter,
    companyFilter,
    isFormOpen,
    selectedNumber,
    formLoading,
    setSearchTerm,
    setSourceFilter,
    setCompanyFilter,
    openCreateForm,
    openEditForm,
    closeForm,
    setFormLoading,
  } = useNumberUIStore();

  const companyId = searchParams.get("companyId");

  // Set company filter from URL
  if (companyId && companyFilter !== companyId) {
    setCompanyFilter(companyId);
  }

  const {
    data: numbers = [],
    isLoading: loading,
    error: numbersError,
  } = useQuery<Number[], Error>({
    queryKey: ["numbers"],
    queryFn: () => numberApi.getAllNumbers(),
  });

  const { data: company } = useQuery({
    queryKey: ["company", companyId],
    queryFn: () => companyApi.getCompany(companyId!),
    enabled: !!companyId,
  });

  const createNumberMutation = useMutation({
    mutationFn: (data: CreateNumberData) => numberApi.createNumber(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["numbers"] });
      closeForm();
    },
    onSettled: () => setFormLoading(false),
  });

  const updateNumberMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateNumberData }) =>
      numberApi.updateNumber(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["numbers"] });
      closeForm();
    },
    onSettled: () => setFormLoading(false),
  });

  const deleteNumberMutation = useMutation({
    mutationFn: (numberId: string) => numberApi.deleteNumber(numberId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["numbers"] }),
  });

  const handleCreateNumber = async (data: CreateNumberData) => {
    setFormLoading(true);
    await createNumberMutation.mutateAsync(data).catch(() => undefined);
  };

  const handleUpdateNumber = async (data: UpdateNumberData) => {
    if (!selectedNumber) return;
    setFormLoading(true);
    await updateNumberMutation
      .mutateAsync({ id: selectedNumber.id, data })
      .catch(() => undefined);
  };

  const handleDeleteNumber = async (numberId: string) => {
    await deleteNumberMutation.mutateAsync(numberId).catch(() => undefined);
  };

  const clearCompanyFilter = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("companyId");
    navigate(`${location.pathname}?${newSearchParams.toString()}`);
    setCompanyFilter("all");
  };

  const filteredNumbers = useMemo(() => {
    let filtered = numbers;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (number) =>
          number.e164Number.toLowerCase().includes(searchTerm.toLowerCase()) ||
          number.friendlyName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          number.company?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by source
    if (sourceFilter !== "all") {
      filtered = filtered.filter((number) => number.source === sourceFilter);
    }

    // Filter by company
    if (companyFilter !== "all") {
      if (companyFilter === "unassigned") {
        filtered = filtered.filter((number) => !number.company);
      } else {
        filtered = filtered.filter(
          (number) => number.company?.id === companyFilter
        );
      }
    }

    return filtered;
  }, [numbers, searchTerm, sourceFilter, companyFilter]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getSourceBadgeColor = (source: string) => {
    switch (source) {
      case "twilio":
        return "bg-blue-100 text-blue-800";
      case "telnyx":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Phone Numbers</h1>
          {companyId && company && (
            <div className="flex items-center gap-2 mt-2">
              <p className="text-muted-foreground">
                Filtered by company: {company.name}
              </p>
              <Button variant="ghost" size="sm" onClick={clearCompanyFilter}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        <Dialog
          open={isFormOpen}
          onOpenChange={(open) => {
            if (!open) closeForm();
          }}
        >
          <DialogTrigger asChild>
            <Button onClick={openCreateForm}>
              <Plus className="mr-2 h-4 w-4" />
              Add Number
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {selectedNumber ? "Edit Number" : "Add New Number"}
              </DialogTitle>
            </DialogHeader>
            <NumberForm
              number={selectedNumber || undefined}
              onSubmit={(data) => {
                if (selectedNumber) {
                  handleUpdateNumber(data as UpdateNumberData);
                } else {
                  handleCreateNumber(data as CreateNumberData);
                }
              }}
              onCancel={closeForm}
              isLoading={formLoading}
            />
          </DialogContent>
        </Dialog>
      </div>

      {numbersError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">{numbersError.message}</p>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search numbers, names, or companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="twilio">Twilio</SelectItem>
                <SelectItem value="telnyx">Telnyx</SelectItem>
              </SelectContent>
            </Select>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Numbers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Numbers ({filteredNumbers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredNumbers.length === 0 ? (
            <div className="text-center py-8">
              <Phone className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No numbers found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || sourceFilter !== "all" || companyFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Get started by adding a new phone number"}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E.164 Number</TableHead>
                  <TableHead>Friendly Name</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Purchase Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNumbers.map((number) => (
                  <TableRow key={number.id}>
                    <TableCell className="font-mono">
                      {number.e164Number}
                    </TableCell>
                    <TableCell className="font-medium">
                      {number.friendlyName}
                    </TableCell>
                    <TableCell>
                      <Badge className={getSourceBadgeColor(number.source)}>
                        {number.source}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {number.company ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {number.company.name}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">
                          Unassigned
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {number.locality && number.region
                          ? `${number.locality}, ${number.region}`
                          : number.region || number.locality || ""}
                        {(number.locality || number.region) &&
                          number.country && (
                            <div className="text-muted-foreground">
                              {number.country}
                            </div>
                          )}
                        {!number.locality &&
                          !number.region &&
                          number.country && <div>{number.country}</div>}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(number.boughtAt)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditForm(number)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Number</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete the number{" "}
                                <strong>{number.e164Number}</strong> (
                                {number.friendlyName})? This action cannot be
                                undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteNumber(number.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
